/**
 * Booking flow — availability computation helpers.
 *
 * Pulled out of `pages/Booking.js` (was 1139 lines) so the two heavy
 * async data-loaders live in one place. Both hit the same 4 Supabase
 * tables (barber_hours / barber_date_exceptions / barber_breaks /
 * bookings) and share the same slot-math via `lib/availability`.
 *
 * Exports:
 *   • roundUpToFive(date)      — snap the min-notice cutoff to a
 *                                cleaner :00/:05/:10/... boundary.
 *   • buildDayAvailability({...}) — 14-day calendar for the picked
 *                                barber+service. Detects `no_hours`
 *                                and `fully_booked` empty states.
 *   • buildBarberNextAvailable({...}) — earliest bookable slot per
 *                                barber, one round-trip across all
 *                                barbers, used for the badge on the
 *                                Barber step cards.
 */
import { supabase } from '../supabase';
import {
  hoursForDate,
  computeAvailableSlots,
  formatWallMin,
  applyDateException,
  dateKey,
} from '../availability';

const WEEKDAY_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTH_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

export const roundUpToFive = (d) => {
  const out = new Date(d);
  const mins = out.getMinutes();
  const bump = (5 - (mins % 5)) % 5;
  if (bump === 0 && out.getSeconds() === 0 && out.getMilliseconds() === 0) return out;
  out.setMinutes(mins + (bump === 0 ? 5 : bump), 0, 0);
  return out;
};

/**
 * 14-day (default) availability window for a single barber+service.
 * Returns `{ days, emptyReason }` where `emptyReason` is:
 *   • 'no_hours'     — barber has no is_open weekday rows at all
 *   • 'fully_booked' — has hours, but nothing bookable in the window
 *   • null           — happy path (at least one day has slots)
 */
export async function buildDayAvailability({ shop, service, barber }) {
  const DAYS_AHEAD = Math.max(1, Number(barber.booking_window_days) || 14);
  const MIN_NOTICE_HOURS = Math.max(0, Number(barber.min_notice_hours) || 0);
  const now = new Date();
  const leadCutoff = roundUpToFive(new Date(now.getTime() + MIN_NOTICE_HOURS * 3600 * 1000));
  const windowStart = new Date();
  windowStart.setHours(0, 0, 0, 0);
  const windowEnd = new Date(windowStart);
  windowEnd.setDate(windowEnd.getDate() + DAYS_AHEAD);

  const [hoursRes, excRes, breaksRes] = await Promise.all([
    supabase.from('barber_hours')
      .select('barber_id,weekday,start_time,end_time,is_open')
      .eq('barber_id', barber.id),
    supabase.from('barber_date_exceptions')
      .select('date,is_closed,start_time,end_time')
      .eq('barber_id', barber.id)
      .gte('date', dateKey(windowStart))
      .lt('date', dateKey(windowEnd)),
    supabase.from('barber_breaks')
      .select('date,start_time,end_time')
      .eq('barber_id', barber.id)
      .gte('date', dateKey(windowStart))
      .lt('date', dateKey(windowEnd)),
  ]);

  const hoursData = hoursRes.data || [];
  const exceptions = excRes.data || [];
  const breaksData = breaksRes.data || [];
  const excByDate = Object.fromEntries(exceptions.map((e) => [e.date, e]));
  const breaksByDate = {};
  breaksData.forEach((br) => {
    (breaksByDate[br.date] = breaksByDate[br.date] || []).push(br);
  });

  const hoursByBarber = { [barber.id]: hoursData };
  const hasAnyOpenDay = hoursData.some((r) => r.is_open !== false && r.start_time && r.end_time);
  const hasAnyForceOpen = exceptions.some((e) => !e.is_closed);

  const { data: bookingsData, error: bookingsError } = await supabase
    .from('bookings')
    .select('starts_at, ends_at')
    .eq('barber_id', barber.id)
    .gte('starts_at', windowStart.toISOString())
    .lt('starts_at', windowEnd.toISOString())
    .in('status', ['pending', 'confirmed', 'completed']);
  if (bookingsError) throw bookingsError;

  const days = [];
  for (let i = 0; i < DAYS_AHEAD; i++) {
    const day = new Date(windowStart);
    day.setDate(day.getDate() + i);
    const weeklyHours = hoursForDate(hoursByBarber, barber.id, day);
    const h = applyDateException(weeklyHours, excByDate[dateKey(day)]);
    const dayEnd = new Date(day);
    dayEnd.setHours(23, 59, 59, 999);

    const dayBookings = (bookingsData || []).filter((b) => {
      const s = new Date(b.starts_at);
      return s >= day && s <= dayEnd;
    });

    let minStartMin;
    if (day.getTime() <= leadCutoff.getTime()) {
      if (leadCutoff.toDateString() === day.toDateString()) {
        minStartMin = leadCutoff.getHours() * 60 + leadCutoff.getMinutes();
      } else if (leadCutoff.getTime() > day.getTime()) {
        minStartMin = 24 * 60;
      }
    }

    const starts = computeAvailableSlots({
      hours: h,
      durationMin: service.duration_min,
      bookings: dayBookings,
      breaks: breaksByDate[dateKey(day)] || [],
      stepMin: barber.slot_interval_minutes || service.duration_min,
      bufferMin: barber.booking_buffer_minutes || 0,
      minStartMin,
      // Slots respect the barber's configured grid step. We used to
      // add extra "anchored" candidates aligned to each preceding
      // booking's end (rounded up to a hard-coded 5-min quantum),
      // which produced surprise off-grid slot times (e.g. 18:20 next
      // to 18:30 on a 30-min grid). Removed for the customer-facing
      // flow — the shop's "Slot interval" setting is now the single
      // source of truth for slot start times.
    });

    const slots = starts.map((min) => {
      const slotStart = new Date(day);
      slotStart.setHours(Math.floor(min / 60), min % 60, 0, 0);
      const label = formatWallMin(min);
      return { time: label, datetime: slotStart, display: label };
    });

    days.push({
      date: day,
      // Local-timezone YYYY-MM-DD. Using `.toISOString().slice(0,10)`
      // here was off-by-one during BST (local midnight = 23:00 UTC of
      // the previous day) AND caused the DST fall-back day (Oct 25 in
      // 2026) to be silently skipped and the spring-forward day (Mar
      // 28 in 2027) to appear twice.
      key: dateKey(day),
      weekday: WEEKDAY_SHORT[day.getDay()],
      dayNum: day.getDate(),
      monthShort: MONTH_SHORT[day.getMonth()],
      isClosed: !h.open,
      hasSlots: slots.length > 0,
      slots,
    });
  }

  let emptyReason = null;
  if (!hasAnyOpenDay && !hasAnyForceOpen) emptyReason = 'no_hours';
  else if (!days.some((d) => d.hasSlots)) emptyReason = 'fully_booked';

  return { days, emptyReason };
}

/**
 * Per-barber earliest-available lookup. Single query per table so the
 * whole shop's Barber-step badges resolve in one round-trip.
 */
export async function buildBarberNextAvailable({ service, barbers }) {
  const MAX_WINDOW = Math.max(...barbers.map((b) => Number(b.booking_window_days) || 14));
  const now = new Date();
  const windowStart = new Date();
  windowStart.setHours(0, 0, 0, 0);
  const windowEnd = new Date(windowStart);
  windowEnd.setDate(windowEnd.getDate() + MAX_WINDOW);

  const barberIds = barbers.map((b) => b.id);

  const [{ data: hoursRows }, { data: bookingRows }, { data: excRows }, { data: breakRows }] = await Promise.all([
    supabase.from('barber_hours')
      .select('barber_id,weekday,start_time,end_time,is_open')
      .in('barber_id', barberIds),
    supabase.from('bookings')
      .select('barber_id,starts_at,ends_at')
      .in('barber_id', barberIds)
      .gte('starts_at', windowStart.toISOString())
      .lt('starts_at', windowEnd.toISOString())
      .in('status', ['pending', 'confirmed', 'completed']),
    supabase.from('barber_date_exceptions')
      .select('barber_id,date,is_closed,start_time,end_time')
      .in('barber_id', barberIds)
      .gte('date', dateKey(windowStart))
      .lt('date', dateKey(windowEnd)),
    supabase.from('barber_breaks')
      .select('barber_id,date,start_time,end_time')
      .in('barber_id', barberIds)
      .gte('date', dateKey(windowStart))
      .lt('date', dateKey(windowEnd)),
  ]);

  const hoursByBarber = {};
  (hoursRows || []).forEach((r) => { (hoursByBarber[r.barber_id] = hoursByBarber[r.barber_id] || []).push(r); });
  const bookingsByBarber = {};
  (bookingRows || []).forEach((b) => { (bookingsByBarber[b.barber_id] = bookingsByBarber[b.barber_id] || []).push(b); });
  const excByBarberDate = {};
  (excRows || []).forEach((e) => {
    excByBarberDate[e.barber_id] = excByBarberDate[e.barber_id] || {};
    excByBarberDate[e.barber_id][e.date] = e;
  });
  const breaksByBarberDate = {};
  (breakRows || []).forEach((br) => {
    breaksByBarberDate[br.barber_id] = breaksByBarberDate[br.barber_id] || {};
    (breaksByBarberDate[br.barber_id][br.date] = breaksByBarberDate[br.barber_id][br.date] || []).push(br);
  });

  const next = {};
  for (const b of barbers) {
    const rows = hoursByBarber[b.id] || [];
    const excMap = excByBarberDate[b.id] || {};
    const hasAnyOpen = rows.some((r) => r.is_open !== false && r.start_time && r.end_time);
    const hasForceOpen = Object.values(excMap).some((e) => !e.is_closed);
    if (!hasAnyOpen && !hasForceOpen) { next[b.id] = { kind: 'no_hours' }; continue; }

    const barberWindow = Math.max(1, Number(b.booking_window_days) || 14);
    const leadHours = Math.max(0, Number(b.min_notice_hours) || 0);
    const leadCutoff = roundUpToFive(new Date(now.getTime() + leadHours * 3600 * 1000));

    let found = null;
    for (let i = 0; i < barberWindow && !found; i++) {
      const day = new Date(windowStart);
      day.setDate(day.getDate() + i);
      const weeklyHours = hoursForDate({ [b.id]: rows }, b.id, day);
      const h = applyDateException(weeklyHours, excMap[dateKey(day)]);
      if (!h.open) continue;

      const dayEnd = new Date(day);
      dayEnd.setHours(23, 59, 59, 999);
      const dayBookings = (bookingsByBarber[b.id] || []).filter((bk) => {
        const s = new Date(bk.starts_at);
        return s >= day && s <= dayEnd;
      });

      let minStartMin;
      if (leadCutoff.toDateString() === day.toDateString()) {
        minStartMin = leadCutoff.getHours() * 60 + leadCutoff.getMinutes();
      } else if (leadCutoff.getTime() > day.getTime()) {
        minStartMin = 24 * 60;
      }

      const starts = computeAvailableSlots({
        hours: h,
        durationMin: service.duration_min,
        bookings: dayBookings,
        breaks: (breaksByBarberDate[b.id] || {})[dateKey(day)] || [],
        stepMin: b.slot_interval_minutes || service.duration_min,
        bufferMin: b.booking_buffer_minutes || 0,
        minStartMin,
      });
      if (starts.length) {
        const min = starts[0];
        const dt = new Date(day);
        dt.setHours(Math.floor(min / 60), min % 60, 0, 0);
        found = {
          kind: 'available',
          datetime: dt,
          label: `${WEEKDAY_SHORT[day.getDay()]} ${day.getDate()} ${MONTH_SHORT[day.getMonth()]} · ${formatWallMin(min)}`,
          dayKey: dateKey(day),
        };
      }
    }
    next[b.id] = found || { kind: 'fully_booked' };
  }
  return next;
}
